import { supabase } from '../lib/supabase';

class SongFileService {
  /**
   * Upload MIDI or MP3 file to storage (song-level only)
   * @param {File} file - The file to upload
   * @param {string} songId - The song ID
   * @param {string} fileType - 'midi' or 'mp3'
   * @returns {Promise<{data: {path: string}, error: null} | {data: null, error: Error}>}
   */
  async uploadSongFile(file, songId, fileType = 'midi') {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      if (!['midi', 'mp3']?.includes(fileType)) {
        throw new Error('File type must be either midi or mp3');
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create file path structure (song-level only)
      const timestamp = Date.now();
      const fileExtension = file?.name?.split('.')?.pop();
      const filePath = `${songId}/${fileType}_${timestamp}.${fileExtension}`;

      // Upload file to storage
      const { data, error } = await supabase?.storage?.from('song-files')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      return { data: { path: data?.path }, error: null };
    } catch (error) {
      console.error('Error uploading song file:', error);
      return { data: null, error };
    }
  }

  /**
   * Update song record with file path
   * @param {string} songId - The song ID
   * @param {string} filePath - The storage file path
   * @param {string} fileType - 'midi' or 'mp3'
   * @returns {Promise<{data: any, error: null} | {data: null, error: Error}>}
   */
  async updateSongFilePath(songId, filePath, fileType) {
    try {
      const columnName = fileType === 'midi' ? 'midi_file_path' : 'mp3_file_path';
      
      const { data, error } = await supabase?.from('songs')?.update({ [columnName]: filePath })?.eq('id', songId)?.select()?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error updating song file path:', error);
      return { data: null, error };
    }
  }

  /**
   * @deprecated Character-specific files have been consolidated to song level
   */
  async updateCharacterAnalysisFilePath(characterAnalysisId, filePath, fileType) {
    console.warn('updateCharacterAnalysisFilePath is deprecated - files are now stored at song level only');
    return { data: null, error: new Error('Character-specific files are no longer supported') };
  }

  /**
   * Get signed URL for private song file
   * @param {string} filePath - The storage file path
   * @returns {Promise<string | null>}
   */
  async getSongFileUrl(filePath) {
    try {
      if (!filePath) return null;

      // Parse the file path to get directory and filename
      const pathParts = filePath?.split('/');
      const fileName = pathParts?.pop();
      const directory = pathParts?.join('/');

      // First check if the file exists in storage
      const { data: fileList, error: listError } = await supabase?.storage
        ?.from('song-files')
        ?.list(directory || '', {
          limit: 100, // Increase limit to catch the file
          search: fileName
        });

      if (listError) {
        // Silently handle list errors - file might not exist
        return null;
      }

      // Check if file exists in the list
      const fileExists = fileList?.some(file => file?.name === fileName);

      if (!fileExists || !fileList || fileList?.length === 0) {
        // File doesn't exist in storage - return null silently
        return null;
      }

      // File exists, now get the signed URL
      const { data, error } = await supabase?.storage
        ?.from('song-files')
        ?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        // Silently return null for any errors including "Load failed"
        return null;
      }

      return data?.signedUrl;
    } catch (error) {
      // Silently handle all errors including network failures
      return null;
    }
  }

  /**
   * Delete song file from storage
   * @param {string} filePath - The storage file path
   * @returns {Promise<{data: any, error: null} | {data: null, error: Error}>}
   */
  async deleteSongFile(filePath) {
    try {
      if (!filePath) {
        throw new Error('No file path provided');
      }

      const { data, error } = await supabase?.storage?.from('song-files')?.remove([filePath]);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error deleting song file:', error);
      return { data: null, error };
    }
  }

  /**
   * Remove file path from song record
   * @param {string} songId - The song ID
   * @param {string} fileType - 'midi' or 'mp3'
   * @returns {Promise<{data: any, error: null} | {data: null, error: Error}>}
   */
  async removeSongFilePath(songId, fileType) {
    try {
      const columnName = fileType === 'midi' ? 'midi_file_path' : 'mp3_file_path';
      
      const { data, error } = await supabase?.from('songs')?.update({ [columnName]: null })?.eq('id', songId)?.select()?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error removing song file path:', error);
      return { data: null, error };
    }
  }

  /**
   * @deprecated Character-specific files have been consolidated to song level
   */
  async removeCharacterAnalysisFilePath(characterAnalysisId, fileType) {
    console.warn('removeCharacterAnalysisFilePath is deprecated - files are now stored at song level only');
    return { data: null, error: new Error('Character-specific files are no longer supported') };
  }
}

const songFileServiceInstance = new SongFileService();
export default songFileServiceInstance;